import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Submission, Participant, Task, UserProfile } from '../types';
import SubmissionCard from './SubmissionCard';
import FilterBar from './FilterBar';
import ParticipantSidebar from './ParticipantSidebar';
import FloatingSynthesisBar from './FloatingSynthesisBar';
import NudgeModal from './NudgeModal';
import PushNotificationModal from './PushNotificationModal';
import TagModal from './TagModal';
import ParticipantProfileModal from './ParticipantProfileModal';
import { createNudge, updateTeamMemberActivity, getAllProjectParticipants } from '../services/firestoreService';
import { sendPushNotification, sendPushNotificationToGroup } from '../services/notificationService';
import { getCurrentUser } from '../services/authService';
import { useAuthContext } from '../contexts/AuthContext';
import { useAppData } from '../contexts/AppDataContext';
import { useParticipants } from '../hooks/useParticipants';
import { useSubmissions } from '../hooks/useSubmissions';
import Avatar from './Avatar';
import {
    GridIcon, ListIcon, FilterIcon, SearchIcon, UserIcon,
    XIcon, LayoutDashboardIcon, DatabaseIcon,
    CheckSquareIcon, BellIcon, StarIcon, MenuIcon, ClockIcon,
    ChevronLeftIcon, ChevronRightIcon, UsersIcon, ChevronDownIcon, CheckIcon
} from './icons';

interface MissionControlViewProps {
    projectId: string;
    setSelectedSubmission: (submission: Submission | null) => void;
    tasks: Task[];
    onUpdateSubmission: (id: string, updates: Partial<Submission>) => Promise<void>;
    onBatchUpdate: (ids: string[], updates: Partial<Submission>) => Promise<void>;
    participantGroups?: any[];
    onSelectParticipant?: (id: string | null) => void;
    users?: UserProfile[];
    onShowSuccess?: (message: string) => void;
}

const MissionControlView: React.FC<MissionControlViewProps> = ({
    projectId,
    setSelectedSubmission,
    tasks,
    onUpdateSubmission,
    onBatchUpdate,
    participantGroups = [],
    users = [],
    onShowSuccess
}) => {
    const { claimsOrgId, claimsRole, userProfile } = useAuthContext();
    const { appData } = useAppData();
    // Global Filter State
    const [mediaTypeFilter, setMediaTypeFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [sortBy, setSortBy] = useState<string>('date-desc');

    // Mission Control State
    const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedTaskGroupId, setSelectedTaskGroupId] = useState<string | null>(null);
    const [innerSelectedParticipantId, setInnerSelectedParticipantId] = useState<string | null>(null);
    const [innerTaskGroupParticipantId, setInnerTaskGroupParticipantId] = useState<string | null>(null);
    const [memberSearchTerm, setMemberSearchTerm] = useState<string>('');
    const [taskGroupSearchTerm, setTaskGroupSearchTerm] = useState<string>('');
    const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
    const [isTaskGroupMemberDropdownOpen, setIsTaskGroupMemberDropdownOpen] = useState(false);
    const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<Set<string>>(new Set());
    const [synthesisFilter, setSynthesisFilter] = useState<'all' | 'unreviewed' | 'starred' | 'reviewed'>('all');

    // Nudge/Push State
    const [isNudgeModalOpen, setIsNudgeModalOpen] = useState(false);
    const [isPushModalOpen, setIsPushModalOpen] = useState(false);
    const [isGroupPushModalOpen, setIsGroupPushModalOpen] = useState(false);
    const [isGlobalPushModalOpen, setIsGlobalPushModalOpen] = useState(false);
    const [isGlobalNudgeModalOpen, setIsGlobalNudgeModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [taskGridHeight, setTaskGridHeight] = useState(180);
    const [isResizing, setIsResizing] = useState(false);
    const [isTaskGridCollapsed, setIsTaskGridCollapsed] = useState(true);
    const [hoveredTaskInfo, setHoveredTaskInfo] = useState<{ rect: DOMRect, prompt: string } | null>(null);

    // Pagination Hooks
    const {
        participants: paginatedParticipants,
        fetchNextPage: fetchNextParticipants,
        hasMore: hasMoreParticipants,
        initialLoading: loadingParticipants,
        error: participantsError
    } = useParticipants(projectId, claimsOrgId, claimsRole);

    // Get task IDs if a task group is selected
    const selectedTaskIds = useMemo(() => {
        if (!selectedTaskGroupId) return undefined;
        return tasks
            .filter(t => (t.customGroup || 'Uncategorized') === selectedTaskGroupId)
            .map(t => t.id);
    }, [selectedTaskGroupId, tasks]);

    const {
        submissions: paginatedSubmissions,
        fetchNextPage: fetchNextSubmissions,
        hasMore: hasMoreSubmissions,
        initialLoading: loadingSubmissions,
        error: submissionsError,
        updateLocal: updatePaginatedLocal
    } = useSubmissions(projectId, claimsOrgId, claimsRole, {
        participantId: selectedParticipantId || undefined,
        mediaType: mediaTypeFilter,
        taskIds: selectedTaskIds
    });

    // Unfiltered submissions for the current project to power Sidebar counts
    // (This ignores the selected participant filter but still uses the media filter)
    const {
        submissions: allProjectSubmissions,
        updateLocal: updateAllLocal
    } = useSubmissions(projectId, claimsOrgId, claimsRole, {
        mediaType: mediaTypeFilter
    });

    // Effective Data
    const participantsList = paginatedParticipants;
    const submissions = paginatedSubmissions;

    // Optimistic local update: patch both paginated and sidebar hook arrays instantly
    const handleOptimisticUpdate = useCallback(async (id: string, updates: Partial<Submission>) => {
        updatePaginatedLocal(id, updates);
        updateAllLocal(id, updates);
        await onUpdateSubmission(id, updates);
        // Update researcher presence on review actions
        if (updates.isReviewed !== undefined && userProfile?.email && appData) {
            const me = appData.teamMembers.find(m => m.email === userProfile.email);
            if (me) updateTeamMemberActivity(me.id, projectId).catch(() => {});
        }
    }, [updatePaginatedLocal, updateAllLocal, onUpdateSubmission, userProfile?.email, appData, projectId]);

    const participantsMap = useMemo(() => {
        return new Map(paginatedParticipants.map(p => [p.id, p]));
    }, [paginatedParticipants]);


    // Modal Visibility Logic
    const isAnyModalOpen = isNudgeModalOpen || isPushModalOpen || isGroupPushModalOpen ||
        isGlobalPushModalOpen || isGlobalNudgeModalOpen || isProfileModalOpen || isTagModalOpen;

    // Responsive Sidebar State
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // Researcher presence — update lastActiveAt on mount (debounced, max once per 5 min)
    useEffect(() => {
        if (!userProfile?.uid || !appData) return;
        const me = appData.teamMembers.find(m => m.email === userProfile.email);
        if (!me) return;
        const lastTime = me.lastActiveAt?.toDate ? me.lastActiveAt.toDate().getTime() : 0;
        if (Date.now() - lastTime < 300000) return; // skip if updated < 5 min ago
        updateTeamMemberActivity(me.id, projectId).catch(() => {});
    }, [userProfile?.uid, appData?.teamMembers?.length, projectId]);

    // Clear selection when changing main filters or participant
    useEffect(() => {
        setSelectedSubmissionIds(new Set());
    }, [selectedParticipantId, selectedGroupId, selectedTaskGroupId, innerSelectedParticipantId, innerTaskGroupParticipantId, mediaTypeFilter]);

    // Handlers
    const handleParticipantSelect = (id: string | null) => {
        setSelectedParticipantId(id);
        setSelectedGroupId(null);
        setSelectedTaskGroupId(null);
        setIsMobileSidebarOpen(false);
    };

    const handleGroupSelect = (id: string | null) => {
        setSelectedGroupId(id);
        setSelectedParticipantId(null);
        setSelectedTaskGroupId(null);
        setIsMobileSidebarOpen(false);
    };

    const handleTaskGroupSelect = (id: string | null) => {
        setSelectedTaskGroupId(id);
        setSelectedParticipantId(null);
        setSelectedGroupId(null);
        setInnerTaskGroupParticipantId(null);
        setIsMobileSidebarOpen(false);
    };

    // Resizing logic for Task Grid
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            // Calculate new height based on mouse position relative to a reference
            // For simplicity, we can just use the delta or a fixed offset if we know the start
            const newHeight = Math.max(80, Math.min(600, e.clientY - 250)); // Crude calculation
            setTaskGridHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const taskMap = useMemo(() => {
        return new Map(tasks.map(t => [t.id, t.title || t.prompt]));
    }, [tasks]);

    const participants = useMemo(() => Array.from(participantsMap.values()), [participantsMap]);

    // Main Filtering Logic
    const filteredSubmissions = useMemo(() => {
        return submissions.filter(submission => {
            // 1. Participant/Group Filter
            if (selectedParticipantId && submission.participantId !== selectedParticipantId) {
                return false;
            }

            if (selectedGroupId) {
                const group = participantGroups.find(g => g.id === selectedGroupId);
                if (group) {
                    if (innerSelectedParticipantId) {
                        if (submission.participantId !== innerSelectedParticipantId) return false;
                    } else if (!group.participantIds.includes(submission.participantId)) {
                        return false;
                    }
                }
            }

            if (selectedTaskGroupId) {
                const task = tasks.find(t => t.id === submission.taskId);
                const groupName = task?.customGroup || 'Uncategorized';
                if (groupName !== selectedTaskGroupId) return false;

                if (innerTaskGroupParticipantId) {
                    if (submission.participantId !== innerTaskGroupParticipantId) return false;
                }
            }

            // 2. Search & Media Type
            const participant = participantsMap.get(submission.participantId);
            const mediaTypeMatch = mediaTypeFilter === 'all' || submission.mediaType === mediaTypeFilter;
            const searchTermMatch = searchTerm === '' ||
                (participant && participant.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                submission.notes.toLowerCase().includes(searchTerm.toLowerCase()) ||
                submission.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

            if (!mediaTypeMatch || !searchTermMatch) return false;

            // 3. Synthesis Filter
            if (synthesisFilter === 'unreviewed' && submission.isReviewed) return false;
            if (synthesisFilter === 'starred' && !submission.isStarred) return false;
            if (synthesisFilter === 'reviewed' && !submission.isReviewed) return false;

            return true;
        }).sort((a, b) => {
            switch (sortBy) {
                case 'date-asc':
                    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                case 'participant-asc': {
                    const participantA = participantsMap.get(a.participantId);
                    const participantB = participantsMap.get(b.participantId);
                    const nameA = (participantA && participantA.name) || '';
                    const nameB = (participantB && participantB.name) || '';
                    return nameA.localeCompare(nameB);
                }
                case 'tags-asc': {
                    const tagsA = a.tags;
                    const tagsB = b.tags;
                    if (tagsA.length > 0 && tagsB.length === 0) return -1;
                    if (tagsA.length === 0 && tagsB.length > 0) return 1;
                    return (tagsA[0] || '').localeCompare(tagsB[0] || '');
                }
                case 'date-desc':
                default:
                    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
            }
        });
    }, [submissions, participantsMap, selectedParticipantId, selectedGroupId, innerSelectedParticipantId, mediaTypeFilter, searchTerm, sortBy, synthesisFilter, participantGroups]);

    // Group redo submissions under their parent — redos are hidden from top level
    const { topLevelSubmissions, redosByParent } = useMemo(() => {
        const redoMap = new Map<string, typeof filteredSubmissions>();
        const topLevel: typeof filteredSubmissions = [];
        for (const sub of filteredSubmissions) {
            if (sub.parentSubmissionId && sub.submissionType === 'redo') {
                const arr = redoMap.get(sub.parentSubmissionId) || [];
                arr.push(sub);
                redoMap.set(sub.parentSubmissionId, arr);
            } else {
                topLevel.push(sub);
            }
        }
        return { topLevelSubmissions: topLevel, redosByParent: redoMap };
    }, [filteredSubmissions]);

    // Focus Mode Logic: Progress & Tasks
    const focusParticipantData = useMemo(() => {
        if (!selectedParticipantId) return null;

        const participantSubmissions = submissions.filter(s => s.participantId === selectedParticipantId);
        const submittedTaskIds = new Set(participantSubmissions.map(s => s.taskId).filter(Boolean));

        const assignedTasks = tasks.filter(task => {
            // 1. If user already completed it, it's relevant
            if (submittedTaskIds.has(task.id)) return true;

            // 2. If no specific assignments, it's for everyone
            if (task.assignedParticipantIds && task.assignedParticipantIds.length > 0) {
                return task.assignedParticipantIds.includes(selectedParticipantId);
            }
            return true;
        });

        const taskProgress = assignedTasks.map(task => {
            const isDone = submittedTaskIds.has(task.id);
            return { ...task, isDone };
        });

        const completedCount = taskProgress.filter(t => t.isDone).length;
        const totalCount = taskProgress.length;
        const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

        return { taskProgress, completedCount, totalCount, progressPercentage };
    }, [selectedParticipantId, submissions, tasks]);

    const focusGroupData = useMemo(() => {
        if (!selectedGroupId) return null;

        const group = participantGroups.find(g => g.id === selectedGroupId);
        if (!group) return null;

        const groupSubmissions = submissions.filter(s => group.participantIds.includes(s.participantId));
        const submittedTaskIds = new Set(groupSubmissions.map(s => s.taskId).filter(Boolean));

        const taskProgress = tasks.map(task => {
            const isDone = submittedTaskIds.has(task.id);
            return { ...task, isDone };
        });

        const completedCount = taskProgress.filter(t => t.isDone).length;
        const totalCount = taskProgress.length;
        const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

        return { group, taskProgress, completedCount, totalCount, progressPercentage };
    }, [selectedGroupId, participantGroups, submissions, tasks]);

    const focusTaskGroupData = useMemo(() => {
        if (!selectedTaskGroupId) return null;

        const taskGroupTasks = tasks.filter(t => (t.customGroup || 'Uncategorized') === selectedTaskGroupId);
        const taskGroupSubmissions = submissions.filter(s => {
            const task = tasks.find(t => t.id === s.taskId);
            return (task?.customGroup || 'Uncategorized') === selectedTaskGroupId;
        });

        // Unique participants who have submissions in this group
        const groupParticipantIds = new Set(taskGroupSubmissions.map(s => s.participantId));
        // Also include all participants for overall progress tracking if needed, 
        // but typically task groups might only apply to specific people.
        // For now let's use all active participants as the denominator for progress.
        const totalParticipants = participantsMap.size;

        const taskProgress = taskGroupTasks.map(task => {
            const completions = taskGroupSubmissions.filter(s => s.taskId === task.id).length;
            const isFullyDone = completions >= totalParticipants && totalParticipants > 0;
            return { ...task, completions, isFullyDone };
        });

        const totalExpectedCompletions = taskGroupTasks.length * totalParticipants;
        const currentCompletions = taskGroupSubmissions.length;
        const progressPercentage = totalExpectedCompletions > 0 ? Math.round((currentCompletions / totalExpectedCompletions) * 100) : 0;

        return {
            name: selectedTaskGroupId,
            taskProgress,
            participantCount: groupParticipantIds.size,
            totalCompletions: currentCompletions,
            expectedCompletions: totalExpectedCompletions,
            progressPercentage
        };
    }, [selectedTaskGroupId, tasks, submissions, participantsMap]);

    const groupParticipants = useMemo(() => {
        if (!selectedGroupId) return [];
        const group = participantGroups.find(g => g.id === selectedGroupId);
        if (!group) return [];
        return group.participantIds
            .map(id => participantsMap.get(id))
            .filter((p): p is Participant => !!p);
    }, [selectedGroupId, participantGroups, participantsMap]);

    const filteredGroupParticipants = useMemo(() => {
        if (!memberSearchTerm) return groupParticipants;
        return groupParticipants.filter(p =>
            p.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
            (p.occupation && p.occupation.toLowerCase().includes(memberSearchTerm.toLowerCase()))
        );
    }, [groupParticipants, memberSearchTerm]);

    const taskGroupParticipants = useMemo(() => {
        if (!selectedTaskGroupId) return [];
        const taskGroupSubmissions = submissions.filter(s => {
            const task = tasks.find(t => t.id === s.taskId);
            return (task?.customGroup || 'Uncategorized') === selectedTaskGroupId;
        });
        const participantIds = Array.from(new Set(taskGroupSubmissions.map(s => s.participantId)));
        return participantIds
            .map(id => participantsMap.get(id))
            .filter((p): p is Participant => !!p)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedTaskGroupId, submissions, tasks, participantsMap]);

    const filteredTaskGroupParticipants = useMemo(() => {
        if (!taskGroupSearchTerm) return taskGroupParticipants;
        return taskGroupParticipants.filter(p =>
            p.name.toLowerCase().includes(taskGroupSearchTerm.toLowerCase()) ||
            (p.occupation && p.occupation.toLowerCase().includes(taskGroupSearchTerm.toLowerCase()))
        );
    }, [taskGroupParticipants, taskGroupSearchTerm]);

    // Handlers
    const handleToggleSelection = (id: string) => {
        const newSet = new Set(selectedSubmissionIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedSubmissionIds(newSet);
    };

    const handleBatchReview = async () => {
        const selectedItems = submissions.filter(s => selectedSubmissionIds.has(s.id));
        const allReviewed = selectedItems.every(s => s.isReviewed);
        const newStatus = !allReviewed;

        await onBatchUpdate(Array.from(selectedSubmissionIds), { isReviewed: newStatus });
        setSelectedSubmissionIds(new Set());
    };

    const handleBatchStar = async () => {
        await onBatchUpdate(Array.from(selectedSubmissionIds), { isStarred: true });
        setSelectedSubmissionIds(new Set());
    };

    const handleBatchTag = async (newTags: string[]) => {
        const selectedSubmissions = submissions.filter(s => selectedSubmissionIds.has(s.id));

        // For each submission, append new tags to existing tags
        const updatePromises = Array.from(selectedSubmissionIds).map(async (id) => {
            const submission = selectedSubmissions.find(s => s.id === id);
            if (submission) {
                const updatedTags = [...new Set([...submission.tags, ...newTags])];
                await onBatchUpdate([id], { tags: updatedTags });
            }
        });

        await Promise.all(updatePromises);
        setSelectedSubmissionIds(new Set());
        setIsTagModalOpen(false);
    };

    const handleClearSelection = () => {
        setSelectedSubmissionIds(new Set());
    };

    const handleSendNudge = async (message: string, taskId?: string) => {
        if (!selectedParticipantId) return;

        try {
            const currentUser = getCurrentUser();
            if (!currentUser) {
                alert('You must be logged in to send nudges');
                return;
            }

            const participant = participantsMap.get(selectedParticipantId);
            if (!participant) return;

            const orgId = claimsOrgId || '';

            await createNudge({
                organizationId: orgId,
                projectId: participant.projectId,
                participantId: selectedParticipantId,
                message: message,
                sentBy: currentUser.uid,
                sentAt: new Date().toISOString(),
                isRead: false,
                ...(taskId ? { taskId } : {}),
            });

            setIsNudgeModalOpen(false);
            if (onShowSuccess) onShowSuccess('Nudge sent successfully!');
        } catch (error) {
            console.error('Error sending nudge:', error);
        }
    };

    const handleSendPush = async (title: string, body: string) => {
        if (!selectedParticipantId) return;

        try {
            const participant = participantsMap.get(selectedParticipantId);
            if (!participant) return;

            const result = await sendPushNotification(selectedParticipantId, title, body);

            if (result.success) {
                if (onShowSuccess) onShowSuccess('Push notification sent successfully!');
            }
            setIsPushModalOpen(false);
        } catch (error) {
        }
    };

    const handleSendGlobalPush = async (title: string, body: string) => {
        try {
            const allParticipantIds = participants.map(p => p.id);
            if (allParticipantIds.length === 0) return;

            const result = await sendPushNotificationToGroup(allParticipantIds, title, body);

            if (result.success) {
                if (onShowSuccess) onShowSuccess(`Global notification sent to ${result.count} participants!`);
            }
            setIsGlobalPushModalOpen(false);
        } catch (error) {
            console.error('Error sending global push:', error);
        }
    };

    const handleSendGlobalNudge = async (message: string, taskId?: string) => {
        try {
            const currentUser = getCurrentUser();
            if (!currentUser) {
                alert('You must be logged in to send nudges');
                return;
            }

            const orgId = claimsOrgId || '';

            // Fetch ALL project participants, not just the 50-row paginated
            // slice the grid happens to have loaded. Otherwise "Nudge All"
            // silently skips anyone past the current page.
            const allParticipants = await getAllProjectParticipants(projectId, orgId || null);
            if (allParticipants.length === 0) {
                if (onShowSuccess) onShowSuccess('No participants in this project to nudge.');
                setIsGlobalNudgeModalOpen(false);
                return;
            }

            const nudgePromises = allParticipants.map(participant =>
                createNudge({
                    organizationId: orgId,
                    projectId: participant.projectId,
                    participantId: participant.id,
                    message: message,
                    sentBy: currentUser.uid,
                    sentAt: new Date().toISOString(),
                    isRead: false,
                    // Carry the optional task link selected in NudgeModal.
                    // Without this, the "Link to Task" dropdown in the
                    // Nudge-All modal would render but its value was being
                    // silently dropped — every global nudge wrote no taskId.
                    ...(taskId ? { taskId } : {}),
                })
            );

            await Promise.all(nudgePromises);

            setIsGlobalNudgeModalOpen(false);
            if (onShowSuccess) onShowSuccess(`Global nudges sent to ${allParticipants.length} participants!`);
        } catch (error) {
            console.error('Error sending global nudges:', error);
        }
    };

    const handleSendPushGroup = async (title: string, body: string) => {
        if (!selectedGroupId) return;

        try {
            const group = participantGroups.find(g => g.id === selectedGroupId);
            if (!group) return;

            const result = await sendPushNotificationToGroup(group.participantIds, title, body);

            if (result.success) {
                if (onShowSuccess) onShowSuccess(`Group notification sent to ${result.count} members!`);
            }
            setIsGroupPushModalOpen(false);
        } catch (error) {
            console.error('Error sending group push notification:', error);
            alert('Failed to send group push notification.');
        }
    };

    return (
        <div className="flex h-full relative">
            {/* Mobile Sidebar Backdrop */}
            {isMobileSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
                    onClick={() => setIsMobileSidebarOpen(false)}
                />
            )}

            <div className={`fixed inset-y-0 left-0 z-50 md:z-20 transform transition-transform duration-300 md:relative md:translate-x-0 ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex items-stretch`}>
                <ParticipantSidebar
                    participants={participants}
                    groups={participantGroups}
                    submissions={submissions}
                    allSubmissions={allProjectSubmissions}
                    selectedParticipantId={selectedParticipantId}
                    selectedGroupId={selectedGroupId}
                    selectedTaskGroupId={selectedTaskGroupId}
                    onSelectParticipant={handleParticipantSelect}
                    onSelectGroup={handleGroupSelect}
                    onSelectTaskGroup={handleTaskGroupSelect}
                    tasks={tasks}
                    className="h-full shadow-lg md:shadow-soft w-72 md:w-auto md:min-w-[260px] md:max-w-xs"
                    onClose={() => setIsMobileSidebarOpen(false)}
                />
                {hasMoreParticipants && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30">
                        <button
                            onClick={fetchNextParticipants}
                            className="bg-white border border-app-border text-action px-4 py-2 rounded-full text-xs font-bold shadow-md hover:bg-app-bg transition-all flex items-center gap-2"
                        >
                            {loadingParticipants ? (
                                <div className="w-3 h-3 border-2 border-t-action border-app-border rounded-full animate-spin" />
                            ) : null}
                            Load More
                        </button>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col h-full overflow-hidden w-full relative">
                {/* Filter Header */}
                <div className="px-4 sm:px-6 lg:px-8 py-4 pb-2 space-y-4 bg-white z-10 sticky top-0 border-b border-app-border shadow-sm">
                    <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full xl:w-auto xl:flex-1">
                            {/* Mobile Participants Sidebar Toggle */}
                            <button
                                onClick={() => setIsMobileSidebarOpen(true)}
                                className="md:hidden flex items-center justify-center gap-2 bg-white border border-app-border text-action hover:bg-app-bg px-4 py-2 rounded-lg font-bold shadow-soft transition-colors w-full sm:w-auto flex-shrink-0"
                            >
                                <UsersIcon className="w-5 h-5" />
                                <span>View Participants Data</span>
                            </button>

                            <div className="flex-1 w-full min-w-0">
                                <FilterBar
                                    mediaTypeFilter={mediaTypeFilter}
                                    setMediaTypeFilter={setMediaTypeFilter}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    viewMode={viewMode}
                                    setViewMode={setViewMode}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                />
                            </div>
                        </div>

                        {/* Synthesis Filters */}
                        <div className="flex w-full xl:w-auto overflow-x-auto custom-scrollbar flex-shrink-0 gap-1 mt-2 xl:mt-0">
                            <button
                                onClick={() => setSynthesisFilter('all')}
                                className={`flex-1 xl:flex-none px-4 py-2 text-sm rounded-md transition-colors text-center whitespace-nowrap ${synthesisFilter === 'all' ? 'bg-app-bg text-text-ink font-semibold' : 'text-text-muted font-medium hover:bg-app-bg hover:text-text-ink'}`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setSynthesisFilter('unreviewed')}
                                className={`flex-1 xl:flex-none px-4 py-2 text-sm rounded-md transition-colors text-center whitespace-nowrap ${synthesisFilter === 'unreviewed' ? 'bg-app-bg text-text-ink font-semibold' : 'text-text-muted font-medium hover:bg-app-bg hover:text-text-ink'}`}
                            >
                                Unreviewed
                            </button>
                            <button
                                onClick={() => setSynthesisFilter('reviewed')}
                                className={`flex-1 xl:flex-none px-4 py-2 text-sm rounded-md transition-colors text-center whitespace-nowrap ${synthesisFilter === 'reviewed' ? 'bg-app-bg text-text-ink font-semibold' : 'text-text-muted font-medium hover:bg-app-bg hover:text-text-ink'}`}
                            >
                                Reviewed
                            </button>
                            <button
                                onClick={() => setSynthesisFilter('starred')}
                                className={`flex-1 xl:flex-none px-4 py-2 text-sm rounded-md transition-colors text-center whitespace-nowrap ${synthesisFilter === 'starred' ? 'bg-app-bg text-text-ink font-semibold' : 'text-text-muted font-medium hover:bg-app-bg hover:text-text-ink'}`}
                            >
                                Starred
                            </button>
                        </div>
                    </div>

                    {/* Context Header: Focus Mode (Participant) */}
                    {selectedParticipantId && focusParticipantData && !isAnyModalOpen && (
                        <div className="flex flex-col gap-2 mt-1 px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between py-2 border-b border-app-border">
                                <div className="flex items-baseline gap-4">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-semibold text-text-ink">
                                            {participantsMap.get(selectedParticipantId)?.name}
                                        </h3>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setIsProfileModalOpen(true)}
                                                className="bg-app-bg hover:bg-app-bg text-text-faint hover:text-text-ink p-1 rounded-full transition-colors"
                                                title="View Profile"
                                            >
                                                <UserIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => setIsTaskGridCollapsed(!isTaskGridCollapsed)}
                                                className="p-1 hover:bg-app-bg rounded-lg transition-colors text-text-faint hover:text-text-ink"
                                                title={isTaskGridCollapsed ? "Expand Tasks" : "Collapse Tasks"}
                                            >
                                                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isTaskGridCollapsed ? '-rotate-90' : ''}`} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="h-3 w-px bg-app-border" />
                                    <p className="text-sm text-text-muted">
                                        {focusParticipantData.completedCount} / {focusParticipantData.totalCount} Tasks Completed
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsPushModalOpen(true)}
                                        className="bg-app-bg text-text-muted hover:bg-action/10 hover:text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                    >
                                        <BellIcon className="w-3.5 h-3.5" />
                                        Send Push
                                    </button>
                                    <button
                                        onClick={() => setIsNudgeModalOpen(true)}
                                        className="bg-app-bg text-text-muted hover:bg-action/10 hover:text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                    >
                                        <BellIcon className="w-4 h-4" />
                                        Send Nudge
                                    </button>
                                    <button
                                        onClick={() => setSelectedParticipantId(null)}
                                        className="text-[10px] font-bold text-text-faint hover:text-status-warn transition-all px-2 uppercase tracking-widest"
                                    >
                                        Exit Focus
                                    </button>
                                </div>
                            </div>
                            {!isTaskGridCollapsed && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="w-full rounded-full h-1 mt-2 mb-4 relative" style={{ backgroundColor: '#E8E4DF' }}>
                                        <div
                                            className="h-full rounded-full transition-all duration-1000 ease-out relative"
                                            style={{ width: `${focusParticipantData.progressPercentage}%`, backgroundColor: focusParticipantData.progressPercentage >= 100 ? '#6E7A5A' : focusParticipantData.progressPercentage > 0 ? '#7BBFAF' : 'transparent' }}
                                        >
                                        </div>
                                    </div>

                                    {/* Resizable Task Breakdown Grid */}
                                    <div className="relative group/grid">
                                        <div
                                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 bg-app-bg/50 p-4 rounded-xl border border-app-border overflow-y-auto transition-all duration-75"
                                            style={{ height: `${taskGridHeight}px` }}
                                        >
                                            {focusParticipantData.taskProgress.map(task => (
                                                <div
                                                    key={task.id}
                                                    className="flex items-center gap-2 text-[11px] group cursor-default"
                                                    onMouseEnter={(e) => {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setHoveredTaskInfo({ rect, prompt: task.title || task.prompt });
                                                    }}
                                                    onMouseLeave={() => setHoveredTaskInfo(null)}
                                                >
                                                    {task.isDone ? (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-status-ok shrink-0" />
                                                    ) : (
                                                        <div className="w-1.5 h-1.5 rounded-full bg-neutral-200 shrink-0" />
                                                    )}
                                                    <span className={`truncate flex-1 ${task.isDone ? 'text-text-ink font-medium' : 'text-text-faint'}`}>
                                                        {task.title || task.prompt}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Draggable Horizontal Handle */}
                                        <div
                                            onMouseDown={() => setIsResizing(true)}
                                            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-16 h-4 flex items-center justify-center cursor-row-resize z-20 group-hover/grid:opacity-100 opacity-40 transition-opacity"
                                        >
                                            <div className="w-10 h-1 bg-neutral-300 rounded-full group-hover/grid:bg-action/40" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Context Header: Group Focus Mode */}
                    {selectedGroupId && focusGroupData && !isAnyModalOpen && (
                        <div className="flex flex-col gap-2 mt-1 px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between py-2 border-b border-app-border">
                                <div className="flex items-baseline gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 flex items-center justify-center bg-app-bg rounded-lg">
                                            <UsersIcon className="w-4 h-4 text-action" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-text-ink">
                                            {focusGroupData.group.name}
                                        </h3>
                                        <button
                                            onClick={() => setIsTaskGridCollapsed(!isTaskGridCollapsed)}
                                            className="p-1 hover:bg-neutral-100 rounded-lg transition-colors text-action/40 hover:text-action"
                                            title={isTaskGridCollapsed ? "Expand Tasks" : "Collapse Tasks"}
                                        >
                                            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isTaskGridCollapsed ? '-rotate-90' : ''}`} />
                                        </button>
                                    </div>
                                    <div className="h-3 w-px bg-app-border" />
                                    <p className="text-sm text-text-muted">
                                        {focusGroupData.group.participantIds.length} Members
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsGroupPushModalOpen(true)}
                                        className="bg-action/10 text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                    >
                                        <BellIcon className="w-3.5 h-3.5" />
                                        Push Group
                                    </button>
                                    <button
                                        onClick={() => alert(`Nudge sent to group: ${focusGroupData.group.name}`)}
                                        className="bg-action/10 text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                    >
                                        <BellIcon className="w-4 h-4" />
                                        Nudge Group
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedGroupId(null);
                                            setInnerSelectedParticipantId(null);
                                        }}
                                        className="text-[10px] font-bold text-text-faint hover:text-status-warn transition-all px-2 uppercase tracking-widest"
                                    >
                                        Exit Focus
                                    </button>
                                </div>
                            </div>
                            {!isTaskGridCollapsed && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="w-full rounded-full h-1 relative transition-all duration-300" style={{ backgroundColor: '#E8E4DF' }}>
                                        <div
                                            className="h-full rounded-full transition-all duration-1000 ease-out"
                                            style={{ width: `${focusGroupData.progressPercentage}%`, backgroundColor: focusGroupData.progressPercentage >= 100 ? '#6E7A5A' : focusGroupData.progressPercentage > 0 ? '#7BBFAF' : 'transparent' }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Participant Navigation Strip */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 py-3 border-y border-app-border bg-app-bg/50 -mx-5 px-5">
                                <div className="flex-1 relative">
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex-1">
                                            <button
                                                onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
                                                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border border-app-border rounded-xl hover:border-action/40 transition-all text-sm font-medium shadow-sm"
                                            >
                                                <div className="flex items-center gap-3 truncate">
                                                    {innerSelectedParticipantId ? (
                                                        <>
                                                            <Avatar name={participantsMap.get(innerSelectedParticipantId)?.name || ''} avatarUrl={participantsMap.get(innerSelectedParticipantId)?.avatarUrl} id={innerSelectedParticipantId} className="w-6 h-6 rounded-full" />
                                                            <span className="truncate font-bold text-text-ink">
                                                                {participantsMap.get(innerSelectedParticipantId)?.name}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="w-10 h-10 flex items-center justify-center text-action/40">
                                                                <UsersIcon className="w-5 h-5" />
                                                            </div>
                                                            <span className="text-text-ink font-bold text-[13px]">All Members</span>
                                                        </>
                                                    )}
                                                </div>
                                                <ChevronDownIcon className={`w-4 h-4 text-text-faint transition-transform ${isMemberDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isMemberDropdownOpen && (
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-app-border rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <div className="p-2 border-b border-app-border bg-app-bg">
                                                        <div className="relative">
                                                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-faint" />
                                                            <input
                                                                type="text"
                                                                placeholder="Filter members..."
                                                                value={memberSearchTerm}
                                                                onChange={(e) => setMemberSearchTerm(e.target.value)}
                                                                className="w-full pl-9 pr-4 py-2 bg-white border border-app-border rounded-lg text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                                        <button
                                                            onClick={() => {
                                                                setInnerSelectedParticipantId(null);
                                                                setIsMemberDropdownOpen(false);
                                                                setMemberSearchTerm('');
                                                            }}
                                                            className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left group ${!innerSelectedParticipantId ? 'bg-app-bg text-action' : 'text-text-muted hover:bg-app-bg hover:text-text-ink'}`}
                                                        >
                                                            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                                                                <UsersIcon className={`w-5 h-5 ${!innerSelectedParticipantId ? 'text-action' : 'text-text-faint group-hover:text-text-faint'}`} />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className={`text-sm font-bold ${!innerSelectedParticipantId ? 'text-action' : 'text-text-ink'}`}>All Members</span>
                                                                <span className="text-[11px] text-text-muted opacity-80">Entire group submissions</span>
                                                            </div>
                                                        </button>
                                                        {filteredGroupParticipants.map(participant => (
                                                            <button
                                                                key={participant.id}
                                                                onClick={() => {
                                                                    setInnerSelectedParticipantId(participant.id);
                                                                    setIsMemberDropdownOpen(false);
                                                                    setMemberSearchTerm('');
                                                                }}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left group ${innerSelectedParticipantId === participant.id ? 'bg-app-bg text-action' : 'text-text-muted hover:bg-app-bg hover:text-text-ink'}`}
                                                            >
                                                                <div className="w-10 h-10 shrink-0 flex items-center justify-center">
                                                                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className={`w-8 h-8 rounded-full border-2 ${innerSelectedParticipantId === participant.id ? 'border-action/30' : 'border-transparent group-hover:border-action/10'}`} />
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className={`text-sm font-bold ${innerSelectedParticipantId === participant.id ? 'text-action' : 'text-text-ink'}`}>{participant.name}</span>
                                                                    <span className="text-[11px] text-text-muted opacity-80">{participant.occupation || 'Participant'}</span>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-app-border shadow-sm">
                                            <button
                                                onClick={() => {
                                                    const idx = groupParticipants.findIndex(p => p.id === innerSelectedParticipantId);
                                                    if (idx > 0) setInnerSelectedParticipantId(groupParticipants[idx - 1].id);
                                                    else if (idx === -1) setInnerSelectedParticipantId(groupParticipants[groupParticipants.length - 1].id);
                                                    else setInnerSelectedParticipantId(null);
                                                }}
                                                className="p-2 hover:bg-app-bg rounded-lg text-text-muted transition-colors"
                                                title="Previous Participant"
                                            >
                                                <ChevronLeftIcon className="w-5 h-5" />
                                            </button>
                                            <div className="w-px h-6 bg-neutral-100 mx-1" />
                                            <button
                                                onClick={() => {
                                                    const idx = groupParticipants.findIndex(p => p.id === innerSelectedParticipantId);
                                                    if (idx < groupParticipants.length - 1) setInnerSelectedParticipantId(groupParticipants[idx + 1].id);
                                                    else setInnerSelectedParticipantId(null);
                                                }}
                                                className="p-2 hover:bg-app-bg rounded-lg text-text-muted transition-colors"
                                                title="Next Participant"
                                            >
                                                <ChevronRightIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!isTaskGridCollapsed && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300 pb-4">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-text-ink">Group Progress</span>
                                            <span className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider">{focusGroupData.completedCount} / {focusGroupData.totalCount} tasks completed</span>
                                        </div>
                                        <div className="w-full rounded-full h-1 mt-2 relative" style={{ backgroundColor: '#E8E4DF' }}>
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 ease-out relative"
                                                style={{ width: `${focusGroupData.progressPercentage}%`, backgroundColor: focusGroupData.progressPercentage >= 100 ? '#6E7A5A' : focusGroupData.progressPercentage > 0 ? '#7BBFAF' : 'transparent' }}
                                            >
                                            </div>
                                        </div>
                                    </div>

                                    {/* Resizable Task Breakdown Mini-View */}
                                    <div className="relative group/grid">
                                        <div
                                            className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5 bg-app-bg/50 p-3 rounded-xl border border-app-border overflow-y-auto"
                                            style={{ height: `${Math.max(60, taskGridHeight / 2)}px` }}
                                        >
                                            {focusGroupData.taskProgress.map(task => (
                                                <div
                                                    key={task.id}
                                                    className="h-1.5 rounded-full" style={{ backgroundColor: task.isDone ? '#6E7A5A' : '#E8E4DF' }}
                                                    title={task.title || task.prompt}
                                                />
                                            ))}
                                        </div>
                                        <div
                                            onMouseDown={() => setIsResizing(true)}
                                            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-3 flex items-center justify-center cursor-row-resize z-20 group-hover/grid:opacity-100 opacity-40 transition-opacity"
                                        >
                                            <div className="w-8 h-1 bg-neutral-300 rounded-full group-hover/grid:bg-action/40" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Context Header: Task Group Focus Mode */}
                    {selectedTaskGroupId && focusTaskGroupData && !isAnyModalOpen && (
                        <div className="flex flex-col gap-2 mt-1 px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between py-2 border-b border-app-border">
                                <div className="flex items-baseline gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 flex items-center justify-center bg-app-bg rounded-lg">
                                            <CheckSquareIcon className="w-4 h-4 text-action" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-text-ink">
                                            {focusTaskGroupData.name}
                                        </h3>
                                        <button
                                            onClick={() => setIsTaskGridCollapsed(!isTaskGridCollapsed)}
                                            className="p-1 hover:bg-neutral-100 rounded-lg transition-colors text-action/40 hover:text-action"
                                            title={isTaskGridCollapsed ? "Expand Tasks" : "Collapse Tasks"}
                                        >
                                            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isTaskGridCollapsed ? '-rotate-90' : ''}`} />
                                        </button>
                                    </div>
                                    <div className="h-3 w-px bg-app-border" />
                                    <p className="text-sm text-text-muted">
                                        {focusTaskGroupData.taskProgress.length} Tasks • {focusTaskGroupData.participantCount} Active
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setSelectedTaskGroupId(null)}
                                        className="text-[10px] font-bold text-text-faint hover:text-status-warn transition-all px-2 uppercase tracking-widest"
                                    >
                                        Exit Focus
                                    </button>
                                </div>
                            </div>

                            {/* Participant Navigation Strip */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 py-3 border-y border-app-border bg-app-bg/50 -mx-5 px-5">
                                <div className="flex-1 relative">
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex-1">
                                            <button
                                                onClick={() => setIsTaskGroupMemberDropdownOpen(!isTaskGroupMemberDropdownOpen)}
                                                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white border border-app-border rounded-xl hover:border-action/40 transition-all text-sm font-medium shadow-sm"
                                            >
                                                <div className="flex items-center gap-3 truncate">
                                                    {innerTaskGroupParticipantId ? (
                                                        <>
                                                            <Avatar name={participantsMap.get(innerTaskGroupParticipantId)?.name || ''} avatarUrl={participantsMap.get(innerTaskGroupParticipantId)?.avatarUrl} id={innerTaskGroupParticipantId} className="w-6 h-6 rounded-full" />
                                                            <span className="truncate font-bold text-text-ink">
                                                                {participantsMap.get(innerTaskGroupParticipantId)?.name}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="w-10 h-10 flex items-center justify-center text-action/40">
                                                                <UsersIcon className="w-5 h-5" />
                                                            </div>
                                                            <span className="text-text-ink font-bold text-[13px]">All Members</span>
                                                        </>
                                                    )}
                                                </div>
                                                <ChevronDownIcon className={`w-4 h-4 text-text-faint transition-transform ${isTaskGroupMemberDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isTaskGroupMemberDropdownOpen && (
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-app-border rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <div className="p-2 border-b border-app-border bg-app-bg">
                                                        <div className="relative">
                                                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-faint" />
                                                            <input
                                                                type="text"
                                                                placeholder="Filter members..."
                                                                value={taskGroupSearchTerm}
                                                                onChange={(e) => setTaskGroupSearchTerm(e.target.value)}
                                                                className="w-full pl-9 pr-4 py-2 bg-white border border-app-border rounded-lg text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action"
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                                                        <button
                                                            onClick={() => {
                                                                setInnerTaskGroupParticipantId(null);
                                                                setIsTaskGroupMemberDropdownOpen(false);
                                                                setTaskGroupSearchTerm('');
                                                            }}
                                                            className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left group ${!innerTaskGroupParticipantId ? 'bg-app-bg text-action' : 'text-text-muted hover:bg-app-bg hover:text-text-ink'}`}
                                                        >
                                                            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                                                                <UsersIcon className={`w-5 h-5 ${!innerTaskGroupParticipantId ? 'text-action' : 'text-text-faint group-hover:text-text-faint'}`} />
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className={`text-sm font-bold ${!innerTaskGroupParticipantId ? 'text-action' : 'text-text-ink'}`}>All Members</span>
                                                                <span className="text-[11px] text-text-muted opacity-80">Entire task group submissions</span>
                                                            </div>
                                                        </button>
                                                        {filteredTaskGroupParticipants.map(participant => (
                                                            <button
                                                                key={participant.id}
                                                                onClick={() => {
                                                                    setInnerTaskGroupParticipantId(participant.id);
                                                                    setIsTaskGroupMemberDropdownOpen(false);
                                                                    setTaskGroupSearchTerm('');
                                                                }}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left group ${innerTaskGroupParticipantId === participant.id ? 'bg-app-bg text-action' : 'text-text-muted hover:bg-app-bg hover:text-text-ink'}`}
                                                            >
                                                                <div className="w-10 h-10 shrink-0 flex items-center justify-center">
                                                                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className={`w-8 h-8 rounded-full border-2 ${innerTaskGroupParticipantId === participant.id ? 'border-action/30' : 'border-transparent group-hover:border-action/10'}`} />
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className={`text-sm font-bold ${innerTaskGroupParticipantId === participant.id ? 'text-action' : 'text-text-ink'}`}>{participant.name}</span>
                                                                    <span className="text-[11px] text-text-muted opacity-80">{participant.occupation || 'Participant'}</span>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-app-border shadow-sm">
                                            <button
                                                onClick={() => {
                                                    const idx = taskGroupParticipants.findIndex(p => p.id === innerTaskGroupParticipantId);
                                                    if (idx > 0) setInnerTaskGroupParticipantId(taskGroupParticipants[idx - 1].id);
                                                    else if (idx === -1 && taskGroupParticipants.length > 0) setInnerTaskGroupParticipantId(taskGroupParticipants[taskGroupParticipants.length - 1].id);
                                                    else setInnerTaskGroupParticipantId(null);
                                                }}
                                                className="p-2 hover:bg-app-bg rounded-lg text-text-muted transition-colors"
                                                title="Previous Participant"
                                            >
                                                <ChevronLeftIcon className="w-5 h-5" />
                                            </button>
                                            <div className="w-px h-6 bg-neutral-100 mx-1" />
                                            <button
                                                onClick={() => {
                                                    const idx = taskGroupParticipants.findIndex(p => p.id === innerTaskGroupParticipantId);
                                                    if (idx < taskGroupParticipants.length - 1) setInnerTaskGroupParticipantId(taskGroupParticipants[idx + 1].id);
                                                    else setInnerTaskGroupParticipantId(null);
                                                }}
                                                className="p-2 hover:bg-app-bg rounded-lg text-text-muted transition-colors"
                                                title="Next Participant"
                                            >
                                                <ChevronRightIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!isTaskGridCollapsed && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300 pb-4">
                                    <div className={`space-y-4`}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-text-ink">Aggregate Progress</span>
                                            <span className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider">{focusTaskGroupData.totalCompletions} / {focusTaskGroupData.expectedCompletions} total responses</span>
                                        </div>
                                        <div className="w-full rounded-full h-1 mt-2 relative" style={{ backgroundColor: '#E8E4DF' }}>
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 ease-out relative"
                                                style={{ width: `${focusTaskGroupData.progressPercentage}%`, backgroundColor: focusTaskGroupData.progressPercentage >= 100 ? '#6E7A5A' : focusTaskGroupData.progressPercentage > 0 ? '#7BBFAF' : 'transparent' }}
                                            >
                                            </div>
                                        </div>
                                    </div>

                                    <div className="relative group/grid">
                                        <div
                                            className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1 bg-app-bg/50 p-3 rounded-xl border border-app-border overflow-y-auto"
                                            style={{ height: `${Math.max(60, taskGridHeight / 2)}px` }}
                                        >
                                            {focusTaskGroupData.taskProgress.map(task => (
                                                <div
                                                    key={task.id}
                                                    className="h-2 rounded-full" style={{ backgroundColor: task.isFullyDone ? '#6E7A5A' : '#E8E4DF' }}
                                                    title={`${task.title || task.prompt} (${task.completions} completions)`}
                                                />
                                            ))}
                                        </div>
                                        <div
                                            onMouseDown={() => setIsResizing(true)}
                                            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-3 flex items-center justify-center cursor-row-resize z-20 group-hover/grid:opacity-100 opacity-40 transition-opacity"
                                        >
                                            <div className="w-8 h-1 bg-neutral-300 rounded-full group-hover/grid:bg-action/40" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Global Summary Header (When no focus) */}
                    {!selectedParticipantId && !selectedGroupId && !selectedTaskGroupId && !isAnyModalOpen && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 sm:py-2 border-b border-app-border mt-1 px-4 sm:px-6 lg:px-8 gap-4 sm:gap-0">
                            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                                <h3 className="text-xl font-semibold text-text-ink">Project Overview</h3>
                                <div className="hidden sm:block h-3 w-px bg-neutral-200" />
                                <p className="text-sm text-text-muted">
                                    {participants.length} Participants • {submissions.length} Submissions
                                </p>
                            </div>

                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button
                                    onClick={() => setIsGlobalPushModalOpen(true)}
                                    className="flex-1 sm:flex-none justify-center bg-app-bg text-text-muted hover:bg-action/10 hover:text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                >
                                    <BellIcon className="w-3.5 h-3.5" />
                                    Push to All
                                </button>
                                <button
                                    onClick={() => setIsGlobalNudgeModalOpen(true)}
                                    className="flex-1 sm:flex-none justify-center bg-app-bg text-text-muted hover:bg-action/10 hover:text-action px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors active:scale-95"
                                >
                                    <BellIcon className="w-4 h-4" />
                                    Nudge All
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Grid Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-24 custom-scrollbar">
                    {(submissionsError || participantsError) ? (
                        /* A failed query must never look like an empty study —
                           a swallowed missing-index error made this page render
                           blank on prod for weeks. */
                        <div className="flex flex-col items-center justify-center h-64 text-center px-6">
                            <h3 className="text-xl font-medium text-text-ink">Couldn't load data</h3>
                            <p className="text-text-muted mt-2 max-w-lg break-words">
                                {submissionsError || participantsError}
                            </p>
                        </div>
                    ) : loadingSubmissions && paginatedSubmissions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-12 h-12 border-4 border-t-action border-action/20 rounded-full animate-spin mb-4"></div>
                            <p className="text-text-ink font-bold">Fetching Submissions...</p>
                        </div>
                    ) : filteredSubmissions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <div className="bg-app-bg text-text-faint p-5 rounded-full mb-6 shadow-sm border border-app-border">
                                <SearchIcon className="w-10 h-10" />
                            </div>
                            <h3 className="text-xl font-medium text-text-ink leading-relaxed">No items found</h3>
                            <p className="text-text-muted mt-2">Try adjusting your filters or search terms.</p>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3">
                            {topLevelSubmissions.map(submission => {
                                const participant = participantsMap.get(submission.participantId);
                                if (!participant) return null;
                                const taskName = submission.taskId ? taskMap.get(submission.taskId) : undefined;
                                const redoChildren = redosByParent.get(submission.id) || [];

                                return (
                                    <React.Fragment key={submission.id}>
                                        <SubmissionCard
                                            submission={submission}
                                            participant={participant}
                                            onSelect={setSelectedSubmission}
                                            taskName={taskName}
                                            isSelected={selectedSubmissionIds.has(submission.id)}
                                            onToggleSelection={handleToggleSelection}
                                            onToggleStar={(id) => handleOptimisticUpdate(id, { isStarred: !submission.isStarred })}
                                            onToggleReview={(id) => handleOptimisticUpdate(id, { isReviewed: !submission.isReviewed })}
                                        />
                                        {redoChildren.map(redo => {
                                            const redoTaskName = redo.taskId ? taskMap.get(redo.taskId) : undefined;
                                            return (
                                                <SubmissionCard
                                                    key={redo.id}
                                                    submission={redo}
                                                    participant={participant}
                                                    onSelect={setSelectedSubmission}
                                                    taskName={redoTaskName}
                                                    isSelected={selectedSubmissionIds.has(redo.id)}
                                                    onToggleSelection={handleToggleSelection}
                                                    onToggleStar={(id) => handleOptimisticUpdate(id, { isStarred: !redo.isStarred })}
                                                    onToggleReview={(id) => handleOptimisticUpdate(id, { isReviewed: !redo.isReviewed })}
                                                    redoAttempt={redo.attemptNumber || 2}
                                                />
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {topLevelSubmissions.map(submission => {
                                const participant = participantsMap.get(submission.participantId);
                                if (!participant) return null;
                                const taskName = submission.taskId ? taskMap.get(submission.taskId) : undefined;
                                const redoChildren = redosByParent.get(submission.id) || [];

                                return (
                                  <React.Fragment key={submission.id}>
                                    <div
                                        className="rounded-xl py-1.5 px-4 hover:bg-app-bg transition-all cursor-pointer group relative"
                                        onClick={() => setSelectedSubmission(submission)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {/* Selection Checkbox */}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleToggleSelection(submission.id); }}
                                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${selectedSubmissionIds.has(submission.id)
                                                    ? 'bg-action border-action'
                                                    : 'border-app-border hover:border-action/40'
                                                    }`}
                                            >
                                                {selectedSubmissionIds.has(submission.id) && (
                                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </button>

                                            {/* Participant Avatar */}
                                            <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />

                                            {/* Info Column */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="font-bold text-text-ink text-[13px]">{participant.name}</h4>
                                                    {taskName && (
                                                        <>
                                                            <span className="text-text-faint">•</span>
                                                            <span className="text-[11px] text-text-muted truncate max-w-xs">{taskName}</span>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-text-muted">
                                                    <div className="flex items-center gap-1">
                                                        <ClockIcon className="w-3 h-3" />
                                                        <span>{new Date(submission.timestamp).toLocaleDateString()}</span>
                                                    </div>
                                                    <span className="text-text-faint">•</span>
                                                    <span className="capitalize">{submission.mediaType}</span>
                                                    {submission.tags.length > 0 && (
                                                        <>
                                                            <span className="text-text-faint">•</span>
                                                            <div className="flex gap-1">
                                                                {submission.tags.slice(0, 2).map(tag => (
                                                                    <span key={tag} className="px-1.5 py-0.5 bg-neutral-100 text-text-muted border border-app-border rounded-full text-[10px] font-medium">
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                                {submission.tags.length > 2 && (
                                                                    <span className="text-text-faint">+{submission.tags.length - 2}</span>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>


                                            {/* Review Toggle (List View) */}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleOptimisticUpdate(submission.id, { isReviewed: !submission.isReviewed }); }}
                                                className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5 shrink-0 hidden sm:flex
                                                    ${submission.isReviewed
                                                        ? 'bg-status-ok-bg text-status-ok hover:bg-status-ok-bg'
                                                        : 'bg-neutral-100 text-action/50 hover:bg-neutral-200 hover:text-action'}`}
                                            >
                                                <CheckIcon className={`w-3.5 h-3.5 ${submission.isReviewed ? 'text-status-ok' : 'text-action/30'}`} />
                                                {submission.isReviewed ? 'Reviewed' : 'Review'}
                                            </button>

                                            {/* Star Button */}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleOptimisticUpdate(submission.id, { isStarred: !submission.isStarred }); }}
                                                className={`p-1 rounded-full transition-colors flex-shrink-0 ${submission.isStarred
                                                    ? 'text-status-warn hover:text-status-warn'
                                                    : 'text-text-faint hover:text-status-warn'
                                                    }`}
                                            >
                                                <StarIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Redo children — indented under parent */}
                                    {redoChildren.map(redo => {
                                        const redoTaskName = redo.taskId ? taskMap.get(redo.taskId) : undefined;
                                        return (
                                            <div
                                                key={redo.id}
                                                className="rounded-xl py-1.5 px-4 ml-8 hover:bg-app-bg transition-all cursor-pointer group relative"
                                                style={{ borderLeft: '2.5px solid #6E7A5A' }}
                                                onClick={() => setSelectedSubmission(redo)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div style={{ background: '#D4715A', color: 'white', fontSize: 9, fontWeight: 700, borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>
                                                        Redo #{redo.attemptNumber || 2}
                                                    </div>
                                                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[12px] font-semibold text-text-ink">{participant.name}</span>
                                                            {redoTaskName && (
                                                                <>
                                                                    <span className="text-text-faint">•</span>
                                                                    <span className="text-[11px] text-text-muted truncate max-w-xs">{redoTaskName}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="text-[10px] text-text-muted mt-0.5">
                                                            <ClockIcon className="w-3 h-3 inline mr-1" />
                                                            {new Date(redo.timestamp).toLocaleDateString()}
                                                            <span className="text-text-faint mx-1">•</span>
                                                            <span className="capitalize">{redo.mediaType}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleOptimisticUpdate(redo.id, { isReviewed: !redo.isReviewed }); }}
                                                        className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest transition-colors items-center gap-1.5 shrink-0 hidden sm:flex
                                                            ${redo.isReviewed
                                                                ? 'bg-status-ok-bg text-status-ok'
                                                                : 'bg-neutral-100 text-action/50 hover:text-action'}`}
                                                    >
                                                        <CheckIcon className={`w-3.5 h-3.5 ${redo.isReviewed ? 'text-status-ok' : 'text-action/30'}`} />
                                                        {redo.isReviewed ? 'Reviewed' : 'Review'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                  </React.Fragment>
                                );
                            })}
                        </div>
                    )}

                    {hasMoreSubmissions && (
                        <div className="mt-8 mb-12 flex justify-center">
                            <button
                                onClick={fetchNextSubmissions}
                                disabled={loadingSubmissions}
                                className="bg-action text-white px-8 py-3 rounded-xl font-bold text-sm shadow-soft hover:bg-action-dark transition-all flex items-center gap-3 disabled:opacity-50"
                            >
                                {loadingSubmissions ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : null}
                                {loadingSubmissions ? 'Loading...' : 'Load More Submissions'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <FloatingSynthesisBar
                selectedCount={selectedSubmissionIds.size}
                onClear={handleClearSelection}
                onMarkReviewed={handleBatchReview}
                onStar={handleBatchStar}
                onTag={() => setIsTagModalOpen(true)}
            />

            {/* Participant Profile Modal */}
            {
                selectedParticipantId && (
                    (() => {
                        const participant = participantsMap.get(selectedParticipantId);
                        const user = participant ? users.find(u => participant.id.startsWith(u.uid)) : null;
                        const email = user?.email || participant?.email;

                        return (
                            <ParticipantProfileModal
                                isOpen={isProfileModalOpen}
                                onClose={() => setIsProfileModalOpen(false)}
                                participant={participant!}
                                email={email}
                            />
                        );
                    })()
                )
            }

            {
                isNudgeModalOpen && selectedParticipantId && participantsMap.has(selectedParticipantId) && (
                    <NudgeModal
                        participant={participantsMap.get(selectedParticipantId)!}
                        isOpen={isNudgeModalOpen}
                        onClose={() => setIsNudgeModalOpen(false)}
                        onSend={handleSendNudge}
                        tasks={tasks}
                    />
                )
            }

            {
                isPushModalOpen && selectedParticipantId && participantsMap.has(selectedParticipantId) && (
                    <PushNotificationModal
                        targetName={participantsMap.get(selectedParticipantId)!.name}
                        isOpen={isPushModalOpen}
                        onClose={() => setIsPushModalOpen(false)}
                        onSend={handleSendPush}
                    />
                )
            }

            {
                isGroupPushModalOpen && selectedGroupId && (
                    <PushNotificationModal
                        targetName={participantGroups.find(g => g.id === selectedGroupId)?.name || 'Group'}
                        isGroup={true}
                        isOpen={isGroupPushModalOpen}
                        onClose={() => setIsGroupPushModalOpen(false)}
                        onSend={handleSendPushGroup}
                    />
                )
            }

            {
                isGlobalPushModalOpen && (
                    <PushNotificationModal
                        targetName="All Participants"
                        isGroup={true}
                        isOpen={isGlobalPushModalOpen}
                        onClose={() => setIsGlobalPushModalOpen(false)}
                        onSend={handleSendGlobalPush}
                    />
                )
            }

            {
                isGlobalNudgeModalOpen && (
                    <NudgeModal
                        targetName="All Participants"
                        isOpen={isGlobalNudgeModalOpen}
                        onClose={() => setIsGlobalNudgeModalOpen(false)}
                        onSend={handleSendGlobalNudge}
                    />
                )
            }

            {/* Tag Modal */}
            <TagModal
                isOpen={isTagModalOpen}
                onClose={() => setIsTagModalOpen(false)}
                onAddTags={handleBatchTag}
                selectedCount={selectedSubmissionIds.size}
            />

            {/* Floating Tooltip (Fixed Position) */}
            {hoveredTaskInfo && !isAnyModalOpen && (
                <div
                    className="fixed z-[9999] pointer-events-none animate-in fade-in slide-in-from-bottom-1 duration-200"
                    style={{
                        top: hoveredTaskInfo.rect.top - 8,
                        left: hoveredTaskInfo.rect.left + 8,
                        transform: 'translateY(-100%)',
                    }}
                >
                    <div className="bg-neutral-800/95 backdrop-blur-sm text-white text-[10px] py-1.5 px-3 rounded-lg shadow-2xl border border-white/10 whitespace-normal max-w-[240px] leading-tight font-medium relative">
                        {hoveredTaskInfo.prompt}
                        <div className="absolute top-full left-2 -translate-y-px border-[6px] border-transparent border-t-neutral-800/95" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissionControlView;
